import { RotateCw } from 'lucide-react';

import { categoryMeta } from './pendencies-adapter';
import { formatOldestOverdue } from './pendency-metrics';

import type { PersonalPendencyMetricsRaw } from '../../lib/api-client';

/**
 * PendencyMetricsPanel — aba "Métricas" da Tela 3 (Pendências · Item 346 D3).
 *
 * Pulso da PRÓPRIA caixa do atendente (caller-scoped pelo backend): KPIs reais
 * + concentração por categoria. Honest-defer explícito no que o modelo não tem
 * (gráficos SLA, tempo médio, gauge de adiamento, por-origem) — não fabrica.
 */

interface PendencyMetricsPanelProps {
  metrics: PersonalPendencyMetricsRaw | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

export function PendencyMetricsPanel({
  metrics,
  isLoading,
  isError,
  onRetry,
}: PendencyMetricsPanelProps): JSX.Element {
  if (isLoading) {
    return <div className="p-6 text-center text-text-secondary text-sm">Carregando métricas…</div>;
  }
  if (isError || metrics === undefined) {
    return (
      <div className="p-6 text-center text-text-secondary text-sm">
        Não foi possível carregar suas métricas.{' '}
        <button type="button" className="btn btn--ghost btn--sm" onClick={onRetry}>
          <RotateCw size={13} /> Tentar de novo
        </button>
      </div>
    );
  }

  const kpis: { label: string; value: number | string; hint: string; tone?: string }[] = [
    { label: 'Abertas', value: metrics.open, hint: 'na sua caixa agora' },
    {
      label: 'Vencidas',
      value: metrics.overdue,
      hint:
        metrics.overdue > 0
          ? `mais antiga há ${formatOldestOverdue(metrics.oldestOverdueMinutes)}`
          : 'nenhuma',
      ...(metrics.overdue > 0 && { tone: 'var(--danger-fg)' }),
    },
    {
      label: 'Vence hoje',
      value: metrics.dueToday,
      hint: 'prazo no dia',
      ...(metrics.dueToday > 0 && { tone: 'var(--warning-fg)' }),
    },
    { label: 'Próximos 7 dias', value: metrics.next7d, hint: 'prazo na semana' },
    { label: 'Sem prazo', value: metrics.noDue, hint: 'sem deadline' },
    { label: 'Resolvidas (30d)', value: metrics.resolvedLast30d, hint: 'no último mês' },
  ];

  return (
    <div className="overflow-auto" style={{ padding: 'var(--space-6)' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-6)',
        }}
      >
        {kpis.map((k) => (
          <div
            key={k.label}
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-md, 8px)',
              padding: 'var(--space-3) var(--space-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {k.label}
            </span>
            <span
              data-testid="kpi-value"
              style={{
                fontSize: 28,
                fontWeight: 700,
                lineHeight: 1,
                color: k.tone ?? 'var(--text-primary)',
              }}
            >
              {k.value}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary, var(--text-secondary))' }}>
              {k.hint}
            </span>
          </div>
        ))}
      </div>

      {/* Concentração por categoria — onde o backlog ativo se acumula. */}
      <section style={{ marginBottom: 'var(--space-6)' }}>
        <h2
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: 'var(--space-3)',
          }}
        >
          Por categoria
        </h2>
        {metrics.byCategory.length === 0 ? (
          <div className="text-text-secondary text-sm">Nenhuma pendência ativa.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {metrics.byCategory.map((c) => (
              <div
                key={c.category ?? '__null__'}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-2) var(--space-3)',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-sm, 6px)',
                }}
              >
                <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                  {categoryMeta(c.category).label}
                </span>
                <span style={{ display: 'flex', gap: 'var(--space-3)', fontSize: 12 }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{c.open} aberta(s)</span>
                  {c.overdue > 0 && (
                    <span style={{ color: 'var(--danger-fg)', fontWeight: 600 }}>
                      {c.overdue} vencida(s)
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <p style={{ fontSize: 11, color: 'var(--text-secondary)', maxWidth: 560 }}>
        São as métricas da <strong>sua</strong> caixa. Indicadores de SLA, tempo médio de resolução,
        taxa de adiamento e quebra por origem ainda não estão disponíveis nesta visão pessoal — a
        visão do time fica no Painel do Supervisor.
      </p>
    </div>
  );
}
